import clsx from 'clsx';
import { Typography, makeStyles, Tooltip } from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { useEffect, useRef, useState } from 'react';

interface Props {
  className?: string;
  label: string;
  labelSize?: string | number;
  orientation?: 'vertical' | 'horizontal';
  text?: string | number | undefined;
  textSize?: string | number;
  textColor?: string | undefined;
  textPaddingTop?: string | number | undefined;
  maxWidth?: string | number;
  noWrap?: boolean | undefined;
  align?: any | undefined;
  loading?: boolean;
  disabled?: boolean | undefined;
  button?: any | undefined;
}

export const LabeledText = (props: Props) => {
  const {
    className,
    label,
    labelSize = 14,
    orientation = 'vertical',
    text,
    textSize = 20,
    textColor,
    textPaddingTop = 8,
    align,
    maxWidth,
    noWrap,
    loading,
    disabled,
    button,
  } = props;
  const classes = useStyles(
    textColor,
    textPaddingTop,
    labelSize,
    maxWidth,
    textSize,
    orientation
  )();
  const [isOverflowing, setIsOverflowing] = useState(false);
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (textRef!.current)
      setIsOverflowing(
        textRef.current.scrollWidth > textRef.current.clientWidth
      );
  }, [textRef]);

  const renderComponent = () => {
    if (text !== undefined && button == undefined) {
      const content = (
        <Typography
          ref={textRef}
          className={clsx(classes.text, className, {
            [classes.greyText]: disabled,
          })}
          variant="body1"
          noWrap={noWrap}
          align={align ?? 'center'}
        >
          {text}
        </Typography>
      );

      return isOverflowing ? (
        <Tooltip arrow title={text}>
          {content}
        </Tooltip>
      ) : (
        content
      );
    } else if (button !== undefined) {
      return button;
    } else {
      return (
        <Typography
          className={clsx(classes.text, className, {
            [classes.greyText]: disabled,
          })}
          variant="body1"
          noWrap={noWrap}
          align={align ?? 'center'}
        >
          N/A
        </Typography>
      );
    }
  };

  return (
    <div className={classes.container}>
      <Typography
        className={clsx(classes.header, { [classes.greyText]: disabled })}
        variant="body1"
        noWrap={noWrap}
        align={align ?? 'center'}
      >
        {orientation === 'horizontal' ? `${label}:` : label}
      </Typography>
      {loading ? (
        <Skeleton className={classes.skeleton} variant="rect" />
      ) : (
        renderComponent()
      )}
    </div>
  );
};

const useStyles = (
  textColor: string | undefined,
  textPaddingTop: string | number | undefined,
  labelSize: string | number,
  maxWidth: string | number | undefined,
  textSize: string | number,
  orientation: 'vertical' | 'horizontal'
) =>
  makeStyles((theme: any) => ({
    container: {
      display: 'flex',
      flexDirection: orientation === 'vertical' ? 'column' : 'row',
      alignItems: 'center',
      justifyContent: 'center',
    },
    header: {
      fontSize: labelSize,
      opacity: 0.7,
      width: orientation === 'vertical' ? '100%' : undefined,
      color: theme.palette.typeColor.main,
    },
    text: {
      fontSize: textSize,
      paddingTop: orientation === 'vertical' ? textPaddingTop : undefined,
      paddingLeft: orientation === 'horizontal' ? textPaddingTop : undefined,
      fontWeight: 500,
      color: textColor ? textColor : theme.palette.typeColor.main,
      maxWidth: maxWidth,
    },
    skeleton: {
      margin: '13px 15px',
      height: 20,
      width: '90%',
      borderRadius: 30,
    },
    greyText: {
      opacity: 0.5,
    },
  }));
